import { Component, ElementRef, Inject, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { EmailPreview, 
  SendAttachment, 
  PostSendNewEmail, 
  Recipients, 
  RecipientsFormGroup, 
  SendNewEmailDialog, 
  SendNewEmailForm, 
  RelevantRecipient,
  EmployeeRecipient,
  RecipientsGroup,
  RecipientBoxChip,
  ChipType,
  EmailPreviewPost} from '@core/models/case-communication/send-new-email-dialog.model';
import { ConstantsService, ErrorService, LayoutService, NotificationService, SnackbarService } from '@core/services';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { Subject, Observable } from 'rxjs';
import { ViewEmailComponent } from '../view-email/view-email.component';
import { CaseEmailInfo, EmailAttachment, Recipient } from '@core/models/case-communication/case-communication-drawer.model';
import { CaseCommunicationService } from '@core/services/case-communication.service';
import { indicate, insertPlaceholderChips, nameof, removePlaceholderChips, closeDialogOnEscape } from '@shared/helpers';
import { finalize, takeUntil } from 'rxjs/operators';
import { StiiraError } from '@core/models';
import { LeaveAdminStoreService } from '@core/services/leave-admin-store.service';
import { EmailSubjectEditorComponent } from '@modules/case-communication/components/email-subject-editor/email-subject-editor.component';
import { UnsavedChangesComponent } from '../unsaved-changes/unsaved-changes.component';
import { EmailBodyEditorComponent } from '@modules/case-communication/components/email-body-editor/email-body-editor.component';
import { CaseCommunicationStoreService } from '@core/services/case-communication.store.service';
import { position, onDragEnded } from '@shared/helpers/dialog-drag-constraints';
import { UpdateConfirmationComponent } from '../update-confirmation/update-confirmation.component';
import { UpdateConfirmationDialog } from '@core/models/shared/update-confirmation-dialog.model';
import { environment } from '@environments/environment';
import { EmailResult } from '@core/enums/email.enum';

@Component({
    selector: 'app-send-new-email',
    templateUrl: './send-new-email.component.html',
    styleUrls: ['./send-new-email.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class SendNewEmailComponent implements OnInit {
  static readonly componentName = 'SendNewEmailComponent';
  
  public form: FormGroup<SendNewEmailForm>;
  public recipientsOpenState: boolean = true;
  public addCcOpenState: boolean = false;
  public addBccOpenState: boolean = false;
  public emailContentOpenState: boolean = true;
  public attachDocsOpenState: boolean = true;
  public attachments: SendAttachment[] = [];
  public isPreviewLoading$: Subject<boolean> = new Subject<boolean>();
  public isSubmitting$: Subject<boolean>  = new Subject();
  // preview and send share isSubmitting$, so this says which one is running and the template puts the spinner in that button
  public submittingAction: 'preview' | 'send';
  public templateIsLoading$: Subject<boolean> = new Subject<boolean>();
  public recipientsGroups = RecipientsGroup;
  public confirmationShown: boolean = false;
  public position = position;
  public onDragEnded = onDragEnded;

  private formInitValues: any;
  private destroy$: Subject<void> = new Subject<void>();
  public emailTooLarge: string;

  @ViewChild(EmailSubjectEditorComponent) subjectEditor: EmailSubjectEditorComponent;
  @ViewChild(EmailBodyEditorComponent) bodyEditor: EmailBodyEditorComponent;
  @ViewChild('uploadButton', { read: ElementRef }) private uploadButton: ElementRef<HTMLButtonElement>;

  public isMobile$: Observable<boolean>;

  get noChanges(): boolean {
    return JSON.stringify(this.form.value) === JSON.stringify(this.formInitValues);
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: {
      dialogData: SendNewEmailDialog,
      sysText: any,
      viewEmailSysText: any
      unsavedSysText: any
    },
    private layoutService: LayoutService,
    private dialogRef: MatDialogRef<SendNewEmailComponent>,
    private fb: FormBuilder,
    private dialog: MatDialog,
    private service: CaseCommunicationService,
    private ccStore: CaseCommunicationStoreService,
    private errorService: ErrorService,
    private snackbar: SnackbarService,
    private notificationService: NotificationService,
    private laStoreService: LeaveAdminStoreService,
    private constants: ConstantsService
  ) { }

  ngOnInit(): void {
    this.isMobile$ = this.layoutService.isMobile$();

    closeDialogOnEscape(this.dialogRef, this.isSubmitting$, () => this.close());

    this.setupForm();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public onSend(confirm: boolean):void {
    this.submittingAction = 'send';
    this.emailTooLarge = null;

    if (!this.confirmationShown && !confirm && this.attachments.length > 0) {
      this.confirmationShown = true;

      const confDialogData: UpdateConfirmationDialog = {
        title: this.data.sysText.sendNewEmailDialog_confirmTitle,
        firstSentence: this.data.sysText.sendNewEmailDialog_firstSentence,
        messages: [],
        lastSentence: this.data.sysText.sendNewEmailDialog_lastSentence,
        actions: null
      };

      const dialogConfig: MatDialogConfig = {
        id: UpdateConfirmationComponent.componentName,
        panelClass: "mat-dialog-container-mobileWidth",
        maxWidth: "600px",
        disableClose: false,
        data: {
          sysText: this.laStoreService.sysText[this.laStoreService.templates.updateConfirmation],
          updateConfirmationDialog: confDialogData
        }
      };

      this.dialog.open(UpdateConfirmationComponent, dialogConfig)
        .beforeClosed()
        .subscribe((res: boolean) => {
          if (res) {
            this.onSend(true);
          }
        });
      
      return;
    }

    this.service.postSendNewEmail(this.makePostSendNewEmailFormData(this.makePostEmailData()))
      .pipe(indicate(this.isSubmitting$))
      .subscribe(
        (res) => {
          if (res) {
            this.ccStore.caseComDrawer = res;
            this.dialogRef.close();

            if (res.emailResult == EmailResult.success) {
              let snackText = this.data.sysText.sendNewEmailDialog_emailSuccess;
              this.snackbar.open(snackText, this.data.sysText.dismiss);
            }
            else if (res.emailResult == EmailResult.successWithFailure) {
              let errorText = this.data.sysText.sendNewEmailDialog_emailSuccessWithFailure;
              this.notificationService.showErrorToast(errorText);
            }
            else {
              let errorText = this.data.sysText.sendNewEmailDialog_emailFailure;
              this.notificationService.showErrorToast(errorText);
            }
          }
        },
        (err: StiiraError) => {
          this.errorService.setFormModelStateErrors(this.form, err.modelStateErrors);
          if (err.modelStateErrors?.hasOwnProperty(nameof<PostSendNewEmail>("userAttachmentFiles"))) {
            this.emailTooLarge = err.modelStateErrors[nameof<PostSendNewEmail>("userAttachmentFiles")];
          }
        })
  }

  public close(): void {
    if (this.noChanges) {
      this.dialogRef.close();
    }
    else {
      this.openUnsavedChangesDialog();
    }
  }

  public onPreview():void {
    this.submittingAction = 'preview';

    // to get the subject in text form, start with the html,
    // perform the chip replacement, set the content to the 
    // modified content, get the text, then restore the 
    // original html 

    let subjectHtml = this.subjectEditor.subEditor.editor.getContent();
    let subjectChipless = removePlaceholderChips(subjectHtml, this.constants.EDITOR_CHIP, this.data.dialogData.placeholderOptions);
    this.subjectEditor.subEditor.editor.setContent(subjectChipless);
    let subjectText = this.subjectEditor.getEditorText();
    this.subjectEditor.subEditor.editor.setContent(subjectHtml);

    let bodyHtml = this.bodyEditor.bodyEditor.editor.getContent();
    let bodyChipless = removePlaceholderChips(bodyHtml, this.constants.EDITOR_CHIP, this.data.dialogData.placeholderOptions);

    const dto: EmailPreviewPost = {
      caseId: this.laStoreService.caseDetails.leaveInformation.caseId,
      // encode subject/body as base 64 to get around AFD WAF
      subjectBytes: this.textToBase64(subjectText),
      bodyBytes: this.textToBase64(bodyChipless)
    };

    this.service.getEmailPreview(dto)
      .pipe(indicate(this.isSubmitting$))
      .subscribe(res => {
        const dialogConfig: MatDialogConfig = {
          id: ViewEmailComponent.componentName,
          width: '750px',
          disableClose: false,
          data: { 
            sysText: this.data.viewEmailSysText,
            emailData: this.makePreviewEmailData(res),
            isPreview: true
          }
        };
        
        this.dialog.open(ViewEmailComponent, dialogConfig);
    })
  }

  public handleFileInput(files: FileList): void {
    const totalCurrentAttachmentSize = this.attachments.reduce((accumulator, currentValue) => accumulator + currentValue.file.size, 0);
    const addedFileSize = files.item(0).size;
    const maxSize = environment.maxEmailSizeMiB;

    if ((totalCurrentAttachmentSize + addedFileSize) > 1024 * 1024 * maxSize) {
      this.emailTooLarge = this.data.sysText.sendNewEmailDialog_emailTooLarge?.replace("@[maxSize]", maxSize);
      this.notificationService.announceStatus(this.emailTooLarge);
      return;
    }

    this.emailTooLarge = null;
    this.attachments.push({isTemplateFile: false, file: files.item(0)});
    this.notificationService.announceStatus(files.item(0).name);
  }

  public clearFile(file: File): void {
    // the pressed button goes with its file
    this.uploadButton.nativeElement.focus();
    this.emailTooLarge = null;
    this.attachments = this.attachments.filter(a => a.file !== file);
  }

  private setupForm(): void{
    this.form = this.fb.group({
      templates: [],
      to: this.initNewRecipientsGroup(),
      cc: this.initNewRecipientsGroup(this.data.dialogData.automaticCcAddresses),
      bcc: this.initNewRecipientsGroup(this.data.dialogData.automaticBccAddresses),
      subject: [""],
      body: [""]
    });

    this.form.controls.templates.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(templateId => {
        this.handleSelectTemplate(templateId);
      });

    this.form.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(()=>{
        if (!this.ccStore.ccIsEditing && !this.noChanges) {
          this.ccStore.setCcIsEditing = true;
        }
        else if (this.noChanges) {
          this.ccStore.setCcIsEditing = false;
        }
      });

    this.formInitValues = JSON.parse(JSON.stringify(this.form.value));
  }

  private initNewRecipientsGroup(autoAddresses?: string[]): FormGroup {
    return this.fb.group<RecipientsFormGroup>({
      relevantRecipientOptions: new FormControl<string[]>([]),
      employeeRecipientOptionAdd: new FormControl<string>(null),
      employeeRecipientOptions: new FormControl<string[]>([]),
      additionalEmailAddressAdd: new FormControl<string>(null),
      additionalEmailAddresses: new FormControl<string[]>(autoAddresses ?? []),
      recipientBoxChips: new FormControl<RecipientBoxChip[]>([])
    });
  }

  private makePreviewEmailData(replacedData: EmailPreview): CaseEmailInfo {
    const attachments: EmailAttachment[] = this.attachments.map((a, i) => {
      return {
        attachmentId: a.attachmentId ?? -i, // index is used as a temp id for user uploaded files to coordinate loading spinners
        attachmentTitle: a.file.name,
        isDocument: a.isTemplateFile,
        isTemplateFile: a.isTemplateFile,
        file: !a.isTemplateFile ? a.file : null
      }
    });

    return {
      sender: this.data.dialogData.sender,
      recipients: this.makePreviewRecipients(this.form.controls.to),
      subject: replacedData.subject,
      cc: this.makePreviewRecipients(this.form.controls.cc),
      bcc: this.makePreviewRecipients(this.form.controls.bcc),
      bodyHtml: replacedData.bodyHtml,
      sendDateTime: null,
      attachments: attachments
    };
  }

  private makePreviewRecipients(fg: FormGroup<RecipientsFormGroup>): Recipient[] {
    var recipients: Recipient[] = [];

    fg.controls.recipientBoxChips.value.forEach(rbc => {
      const recipient: Recipient = {
        name: rbc.type != ChipType.AdditionalEmail ? rbc.description : null,
        email: rbc.emailAddress
      };

      recipients.push(recipient);
    });
    
    return recipients;
  }

  private makePostEmailData(): PostSendNewEmail {
    // to get the subject and body in text form, start with 
    // the html, perform the chip replacement, set the content 
    // to the modified content, get the text, then restore the 
    // original html 

    let subjectHtml = this.subjectEditor.subEditor.editor.getContent();
    let subjectChipless = removePlaceholderChips(subjectHtml, this.constants.EDITOR_CHIP, this.data.dialogData.placeholderOptions);
    this.subjectEditor.subEditor.editor.setContent(subjectChipless);
    let subjectText = this.subjectEditor.getEditorText();
    this.subjectEditor.subEditor.editor.setContent(subjectHtml);

    let bodyHtml = this.bodyEditor.bodyEditor.editor.getContent();
    let bodyChipless = removePlaceholderChips(bodyHtml, this.constants.EDITOR_CHIP, this.data.dialogData.placeholderOptions);
    this.bodyEditor.bodyEditor.editor.setContent(bodyChipless);
    let bodyText = this.bodyEditor.getEditorText();
    this.bodyEditor.bodyEditor.editor.setContent(bodyHtml);

    const postSendNewEmail: PostSendNewEmail = {
      caseId: this.laStoreService.caseDetails.leaveInformation.caseId,
      to: this.makePostRecipients(this.form.controls.to),
      subject: subjectText,
      cc: this.makePostRecipients(this.form.controls.cc),
      bcc: this.makePostRecipients(this.form.controls.bcc),
      bodyText: bodyText,
      body: bodyChipless,
      bodyBlob: null,
      templateAttachmentIds: this.attachments.filter(a => a.isTemplateFile).map(a => a.attachmentId),
      userAttachmentFiles: this.attachments.filter(a => !a.isTemplateFile).map(a => a.file)
    };

    return postSendNewEmail;
  }

  private makePostRecipients(fg: FormGroup<RecipientsFormGroup>): Recipients {
    var relevantRecipients: RelevantRecipient[] = [];
    var employeeRecipients: EmployeeRecipient[] = [];
    var emailAddresses: string[] = [];

    fg.controls.recipientBoxChips.value.forEach(rbc => {
      switch (rbc.type) {
        case (ChipType.RelevantRecipient): {
          var relevantRecipient: RelevantRecipient = {
            employeeId: rbc.id,
            emailAddressTypeId: rbc.emailAddressTypeId,
            relevantContextId: rbc.relevantContextId };

          relevantRecipients.push(relevantRecipient);         
          break;
        }

        case (ChipType.Contact): {
          var employeeRecipient: EmployeeRecipient = {
            employeeId: rbc.id,
            emailAddressTypeId: rbc.emailAddressTypeId };

          employeeRecipients.push(employeeRecipient);         
          break;
        }

        case (ChipType.AdditionalEmail): {
          emailAddresses.push(rbc.emailAddress);
          break;
        }
      }
    });

    const recipients: Recipients = {
      relevantRecipients: relevantRecipients,
      employeeRecipients: employeeRecipients,
      emailAddresses: emailAddresses
    };

    return recipients;
  }

  private makePostSendNewEmailFormData(dto: PostSendNewEmail): FormData {
    const formData = new FormData();

    formData.append(nameof<PostSendNewEmail>('caseId'), dto.caseId.toString());
    
    formData.append(`${nameof<PostSendNewEmail>('to')}.${nameof<Recipients>('relevantRecipients')}`, JSON.stringify(dto.to.relevantRecipients));
    formData.append(`${nameof<PostSendNewEmail>('to')}.${nameof<Recipients>('employeeRecipients')}`, JSON.stringify(dto.to.employeeRecipients));

    dto.to.emailAddresses.forEach(tea => {
      formData.append(`${nameof<PostSendNewEmail>('to')}.${nameof<Recipients>('emailAddresses')}`, tea);
    });

    formData.append(nameof<PostSendNewEmail>('subject'), dto.subject);

    formData.append(`${nameof<PostSendNewEmail>('cc')}.${nameof<Recipients>('relevantRecipients')}`, JSON.stringify(dto.cc.relevantRecipients));
    formData.append(`${nameof<PostSendNewEmail>('cc')}.${nameof<Recipients>('employeeRecipients')}`, JSON.stringify(dto.cc.employeeRecipients));

    dto.cc.emailAddresses.forEach(ccea => {
      formData.append(`${nameof<PostSendNewEmail>('cc')}.${nameof<Recipients>('emailAddresses')}`, ccea);
    });

    formData.append(`${nameof<PostSendNewEmail>('bcc')}.${nameof<Recipients>('relevantRecipients')}`, JSON.stringify(dto.bcc.relevantRecipients));
    formData.append(`${nameof<PostSendNewEmail>('bcc')}.${nameof<Recipients>('employeeRecipients')}`, JSON.stringify(dto.bcc.employeeRecipients));

    dto.bcc.emailAddresses.forEach(bccea => {
      formData.append(`${nameof<PostSendNewEmail>('bcc')}.${nameof<Recipients>('emailAddresses')}`, bccea);
    });
    
    formData.append(nameof<PostSendNewEmail>('bodyText'), dto.bodyText);
    
    const bodyBlob = new Blob([dto.body], { type: "text/plain;charset=UTF-8"});
    formData.append(nameof<PostSendNewEmail>('bodyBlob'), bodyBlob);
    
    dto.templateAttachmentIds.forEach(taid => {
      formData.append(nameof<PostSendNewEmail>('templateAttachmentIds'), taid.toString());
    });
    
    dto.userAttachmentFiles.forEach(ua => {
      formData.append(ua.name, ua);
    });

    return formData;
  }

  private openUnsavedChangesDialog(): void {
    const dialogConfig: MatDialogConfig = {
      id: UnsavedChangesComponent.componentName,
      width: '300px',
      data: this.data.unsavedSysText
    };

    this.dialog.open(UnsavedChangesComponent, dialogConfig)
      .afterClosed().subscribe((res: boolean) => {
        if (res) {
          this.dialogRef.close();
        }
      });
  }

  private handleSelectTemplate(templateId: number): void {
    if (templateId) {
      this.form.controls.templates.disable({emitEvent: false});

      this.service.getSendNewEmailTemplateDetails(
        {
          caseId: this.laStoreService.caseDetails.leaveInformation.caseId, 
          templateId: templateId
        })
        .pipe(
          indicate(this.templateIsLoading$),
          finalize(()=>{this.form.controls.templates.enable({emitEvent: false})}))
        .subscribe((res)=>{
          this.form.controls.subject
            .patchValue(insertPlaceholderChips(res.subjectText, this.constants.EDITOR_CHIP, this.data.dialogData.placeholderOptions));

          this.form.controls.body
            .patchValue(insertPlaceholderChips(res.bodyHtml, this.constants.EDITOR_CHIP, this.data.dialogData.placeholderOptions));
            
          this.attachments = [];

          res.attachments?.forEach(a => {
            const tempAttach= new File([""] , a.attachmentTitle);
            this.attachments.push({isTemplateFile: true, file: tempAttach, attachmentId: a.attachmentId});
          });
        });
    }
    else {
      this.form.controls.subject.patchValue(null);
      this.form.controls.body.patchValue(null);
      this.attachments = [];
    }
  }

  private textToBase64(value: string): string {
    const bytes = new TextEncoder().encode(value);
    let binary = "";
    for (let i = 0; i < bytes.length; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  }
}